import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
} from "@mui/material";
import { Refresh, Warning, HourglassEmpty } from "@mui/icons-material";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase/config";
import { brandPalette as primaryBlue } from "../config/brandTheme";
import {
  forfeitureLevelsInRange,
  AUTO_FILL_LEVEL_FORFEITURE_MONTHS,
} from "../config/autoFillLevelForfeitureConfig";
import { getLevelForfeitureState } from "../services/autoFill/autoFillLevelForfeitureTracking";

const formatInr = (n) =>
  `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function creditedSerialsFromForfeiture(levelState) {
  const recipients = levelState?.lastForfeiture?.recipients || [];
  return recipients
    .filter((r) => r.credited)
    .map((r) => r.targetSeq)
    .sort((a, b) => a - b);
}

const AutoFillLevel6ForfeitureAdmin = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [holds, setHolds] = useState([]);
  const [programs, setPrograms] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [holdsSnap, progSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, "autoFillLevel6Holds"),
            where("status", "==", "pending")
          )
        ),
        getDocs(collection(db, "autoFillPrograms")),
      ]);
      setHolds(
        holdsSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.nextEnrollmentSeq || 0) - (b.nextEnrollmentSeq || 0))
      );
      setPrograms(progSnap.docs.map((d) => ({ docId: d.id, ...d.data() })));
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const forfeitureEvents = useMemo(() => {
    const rows = [];
    programs.forEach((p) => {
      for (const L of forfeitureLevelsInRange(
        p.currentLevel || 1,
        p
      )) {
        const st = getLevelForfeitureState(p, L);
        if (!st?.forfeitureProcessedAt) continue;
        const summary = st.forfeitureSummary || {};
        const last = st.lastForfeiture || {};
        const creditedSeqs = creditedSerialsFromForfeiture(st);
        const pendingSlots =
          summary.pendingSlots ?? Math.floor((summary.pendingDistribution || 0) / 150);
        rows.push({
          programId: p.programId,
          enrollmentSeq: p.enrollmentSeq,
          email: p.userEmail,
          name: p.userName,
          sourceLevel: L,
          levelReachedAt: st.reachedAt,
          forfeitedAt: st.forfeitureProcessedAt,
          totalRemoved: summary.totalRemovedFromCoPoints ?? last.amount ?? 0,
          distributed: summary.totalDistributed ?? last.distributed ?? 0,
          onHold: summary.pendingDistribution ?? last.onHold ?? 0,
          pendingSlots,
          nextAfpSeq:
            summary.nextAfpEnrollmentSeq ??
            (pendingSlots > 0 ? last.nextEnrollmentSeq : null),
          creditedSeqs,
          holdId: summary.holdId,
        });
      }
    });
    return rows.sort(
      (a, b) =>
        new Date(b.forfeitedAt || 0).getTime() -
        new Date(a.forfeitedAt || 0).getTime()
    );
  }, [programs]);

  const totalOnHold = useMemo(
    () => holds.reduce((s, h) => s + (Number(h.amountOnHold) || 0), 0),
    [holds]
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Warning color="warning" />
        <Typography
          variant={embedded ? "h6" : "h5"}
          sx={{ fontWeight: 700, color: primaryBlue[800] }}
        >
          Level 6–15 forfeiture & redistribution
        </Typography>
        <Button startIcon={<Refresh />} size="small" onClick={load}>
          {embedded ? "Refresh L6 data" : "Refresh"}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Members who did not withdraw tracked <strong>CO Points per level</strong> within{" "}
        {AUTO_FILL_LEVEL_FORFEITURE_MONTHS} months of reaching that level lose that amount
        (primary AFP: levels 6–15; child AFP: levels 1–15)
        from CO Points. It is paid as <strong>₹150 CO Points</strong> per slot to the{" "}
        <strong>next AFP enrollment serials</strong> after theirs. Amounts with no AFP
        yet appear below as <strong>on hold</strong> until new programs are created.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ borderTop: "4px solid #ed6c02" }}>
            <CardContent>
              <Typography variant="overline">Total on hold (no AFP ID yet)</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {formatInr(totalOnHold)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {holds.length} active queue{holds.length === 1 ? "" : "s"} waiting for
                next enrollment serials
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ borderTop: "4px solid #d32f2f" }}>
            <CardContent>
              <Typography variant="overline">Forfeiture cases (L6–L15)</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {forfeitureEvents.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Did not meet level withdrawal deadline
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        <HourglassEmpty sx={{ fontSize: 20, verticalAlign: "middle", mr: 0.5 }} />
        On hold — waiting for next AFP enrollment IDs
      </Typography>
      {holds.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Nothing on hold right now. Either no forfeiture has run yet, or every ₹150
          slot has already been paid to an existing AFP serial.
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Member</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Source AFP</TableCell>
                <TableCell align="right">AFP seq #</TableCell>
                <TableCell align="right">On hold</TableCell>
                <TableCell align="right">₹150 slots waiting</TableCell>
                <TableCell align="right">Next AFP seq to pay</TableCell>
                <TableCell align="right">Already shared</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {holds.map((h) => (
                <TableRow key={h.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {h.sourceUserName || "—"}
                    </Typography>
                    <Typography variant="caption">{h.sourceUserEmail}</Typography>
                  </TableCell>
                  <TableCell>L{h.sourceLevel || 6}</TableCell>
                  <TableCell>{h.sourceProgramId}</TableCell>
                  <TableCell align="right">#{h.sourceEnrollmentSeq}</TableCell>
                  <TableCell align="right">
                    <Chip label={formatInr(h.amountOnHold)} color="warning" size="small" />
                  </TableCell>
                  <TableCell align="right">{h.slotsRemaining}</TableCell>
                  <TableCell align="right">
                    <strong>#{h.nextEnrollmentSeq}</strong>
                  </TableCell>
                  <TableCell align="right">{formatInr(h.totalDistributed || 0)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Divider sx={{ mb: 2 }} />

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Forfeiture history (L6–L15)
      </Typography>
      {forfeitureEvents.length === 0 ? (
        <Alert severity="info">
          No forfeitures recorded yet. They appear after the daily job (or manual run)
          once a member is past the {AUTO_FILL_LEVEL_FORFEITURE_MONTHS}-month window for a level without withdrawing that
          level&apos;s tracked CO Points (while meeting withdrawal rules).
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Member</TableCell>
                <TableCell>Source AFP</TableCell>
                <TableCell align="right">AFP seq</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Reached level</TableCell>
                <TableCell>Forfeited on</TableCell>
                <TableCell align="right">Removed from CO Points</TableCell>
                <TableCell align="right">Shared so far</TableCell>
                <TableCell align="right">Still on hold</TableCell>
                <TableCell>Paid AFP serial #s</TableCell>
                <TableCell>Next AFP serial</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {forfeitureEvents.map((row) => (
                <TableRow key={`${row.programId}-L${row.sourceLevel}`}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.name || "—"}
                    </Typography>
                    <Typography variant="caption">{row.email}</Typography>
                  </TableCell>
                  <TableCell>{row.programId}</TableCell>
                  <TableCell align="right">#{row.enrollmentSeq}</TableCell>
                  <TableCell>L{row.sourceLevel}</TableCell>
                  <TableCell>{formatDate(row.levelReachedAt)}</TableCell>
                  <TableCell>{formatDate(row.forfeitedAt)}</TableCell>
                  <TableCell align="right">{formatInr(row.totalRemoved)}</TableCell>
                  <TableCell align="right">{formatInr(row.distributed)}</TableCell>
                  <TableCell align="right">
                    {row.onHold > 0 ? (
                      <Chip
                        label={formatInr(row.onHold)}
                        color="warning"
                        size="small"
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {row.creditedSeqs.length > 0
                      ? row.creditedSeqs.map((s) => `#${s}`).join(", ")
                      : "None yet (no next AFP IDs)"}
                  </TableCell>
                  <TableCell>
                    {row.nextAfpSeq ? `#${row.nextAfpSeq}` : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AutoFillLevel6ForfeitureAdmin;
